import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Building2, LayoutDashboard, LogOut, ChevronLeft, ArrowLeft, Menu, X, Settings, Wifi, ChevronDown, DollarSign } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { cn } from "../lib/utils";

const OWNER_NAV = [
  { icon: Building2, label: "Clientes", path: "/owner/tenants" },
  { icon: Settings, label: "Configurações", children: [
    { icon: DollarSign, label: "Geral", path: "/owner/configuracoes/geral" },
    { icon: Wifi, label: "Integrações", path: "/owner/configuracoes/integracoes" },
  ]},
];

export default function OwnerLayout({children }) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initials = user?.name
    ? user.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : "O";

  const [openMenus, setOpenMenus] = useState({});
  const toggleMenu = (label) => setOpenMenus(p => ({ ...p, [label]: !p[label] }));

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-slate-900">
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-slate-700 gap-3">
        <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
          <img src="/brand/icon-2.png" alt="Pipe4.me" className="w-9 h-9 object-contain" />
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-bold leading-tight truncate">Pipe4.me</p>
            <p className="text-slate-400 text-[11px] leading-tight">{t("ui.gestao_plataforma")}</p>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex w-7 h-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-700 transition-colors"
        >
          <ChevronLeft className={cn("w-4 h-4 transition-transform duration-200", collapsed && "rotate-180")} />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {OWNER_NAV.map(item => {
          if (item.children) {
            const isOpen = openMenus[item.label] || item.children.some(c => location.pathname.startsWith(c.path));
            const isChildActive = item.children.some(c => location.pathname.startsWith(c.path));
            return (
              <div key={item.label}>
                <button
                  onClick={() => toggleMenu(item.label)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                    isChildActive ? "bg-slate-800 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                  )}
                  title={collapsed ? item.label : undefined}>
                  <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
                  {!collapsed && <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", isOpen && "rotate-180")} />}
                </button>
                {isOpen && !collapsed && (
                  <div className="mt-1 ml-3 pl-3 border-l border-slate-700 space-y-0.5">
                    {item.children.map(child => {
                      const isActive = location.pathname.startsWith(child.path);
                      return (
                        <button key={child.path}
                          onClick={() => { navigate(child.path); setMobileOpen(false); }}
                          className={cn(
                            "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all",
                            isActive ? "bg-indigo-600 text-white font-medium" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                          )}>
                          <child.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                          <span>{child.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          const isActive = location.pathname.startsWith(item.path);
          return (
            <button key={item.path}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                isActive ? "bg-indigo-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"
              )}
              title={collapsed ? item.label : undefined}>
              <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
              {!collapsed && <span>{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Back to CRM */}
      <div className="px-3 pb-4 space-y-2 border-t border-slate-700 pt-3">
        <button
          onClick={() => navigate("/dashboard")}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:bg-slate-800 hover:text-white transition-all"
          title={collapsed ? t("ui.voltar_crm") : undefined}>
          <ArrowLeft className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
          {!collapsed && <span>{t("ui.voltar_crm")}</span>}
        </button>
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:bg-rose-900/40 hover:text-rose-400 transition-all"
          title={collapsed ? t("topbar.logout") : undefined}>
          <LogOut className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
          {!collapsed && <span>{t("topbar.logout")}</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Desktop Sidebar */}
      <aside className={cn(
        "hidden lg:flex flex-col fixed left-0 top-0 h-screen z-30 transition-all duration-200 border-r border-slate-700",
        collapsed ? "w-20" : "w-64"
      )}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 shadow-xl">
            <div className="absolute top-4 right-4">
              <button onClick={() => setMobileOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className={cn("flex-1 flex flex-col min-h-screen min-w-0 transition-all duration-200", collapsed ? "lg:ml-20" : "lg:ml-64")}>
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500">
              <Menu className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-3 pl-3 border-l border-slate-200">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-slate-900 leading-tight">{user?.name || "Owner"}</p>
              <p className="text-xs text-slate-500 leading-tight">{user?.email || ""}</p>
            </div>
            <Avatar className="w-8 h-8">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 md:p-8 min-w-0 overflow-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}
